import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useFormContext } from "react-hook-form";
import { classNames } from "@utils/class-names";
import { ReactComponent as PlusIcon } from "@assets/icons/image-person-add.svg";
import useModalWithData from "@common/hooks/use-modal-with-data";
import { ModalIds } from "@common/models/enums/modal-ids.enum";
import {ImageCropperModalData} from "../../../core/components/image-cropper-modal";

interface ImageUploadFormFieldProps {
    name: string;
    label?: string;
    className?: string;
    isRequired?: boolean;
    forceDisabled?: boolean;
    maxSize?: number;
    acceptedTypes?: string[];
    cropShape?: 'rect' | 'round';
    aspect?: number;
}

const ImageUploadFormField: React.FC<ImageUploadFormFieldProps> = (props: ImageUploadFormFieldProps) => {
    const { register, formState: { errors, isSubmitting, disabled }, setValue, watch } = useFormContext();
    const {
        name,
        label,
        className = '',
        isRequired = false,
        forceDisabled = false,
        maxSize = 5,
        acceptedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/jpg'],
        cropShape = 'rect',
        aspect = 1
    } = props;

    const [preview, setPreview] = useState<string | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const value = watch(name);

    const { showModal } = useModalWithData<ImageCropperModalData>(ModalIds.IMAGE_CROPPER);

    const { ref, onChange: rhfOnChange, ...registerProps } = register(name);

    useEffect(() => {
        if (value instanceof FileList && value.length > 0) {
            const file = value[0];
            const reader = new FileReader();
            reader.onload = (e) => {
                setPreview(e.target?.result as string);
            };
            reader.readAsDataURL(file);
        }
        else if (value instanceof File) {
            const reader = new FileReader();
            reader.onload = (e) => {
                setPreview(e.target?.result as string);
            };
            reader.readAsDataURL(value);
        }
        else if (typeof value === 'string') {
            setPreview(value);
        }
        else if (value === null || value === undefined || (value instanceof FileList && value.length === 0)) {
            setPreview(null);
        }
    }, [value]);

    const handleCropComplete = useCallback((croppedBlob: Blob) => {
        const croppedFile = new File([croppedBlob], `cropped-image-${Date.now()}.jpg`, { type: 'image/jpeg' });
        setValue(name, croppedFile, { shouldValidate: true, shouldDirty: true });
    }, [name, setValue]);

    const handleFileSelect = (file: File) => {
        if (!acceptedTypes.includes(file.type)) {
            alert(`Nieprawidłowy typ pliku. Dozwolone typy: ${acceptedTypes.join(', ')}`);
            return;
        }

        if (file.size > maxSize * 1024 * 1024) {
            alert(`Plik jest za duży. Maksymalny rozmiar: ${maxSize}MB`);
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            const imageUrl = e.target?.result as string;

            showModal({
                imageUrl,
                onCropComplete: handleCropComplete,
                cropShape,
                aspect
            });
        };
        reader.readAsDataURL(file);
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);

        const file = e.dataTransfer.files[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleClick = () => {
        if (!forceDisabled && !disabled && !isSubmitting) {
            fileInputRef.current?.click();
        }
    };

    return (
        <div className={`w-full ${className}`}>
            {label && (
                <label className={classNames(
                    "text-[14px] font-semibold block mb-2 leading-[1.125rem] text-on-surface/80"
                )}>
                    {label}
                    {isRequired && <span className='text-red-500'> *</span>}
                </label>
            )}

            <div
                className={classNames(
                    "relative cursor-pointer transition-colors flex items-center justify-center",
                    (forceDisabled || disabled || isSubmitting) && "opacity-50 cursor-not-allowed"
                )}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={handleClick}
            >
                <input
                    ref={(el) => {
                        ref(el);
                        if (el) fileInputRef.current = el;
                    }}
                    type="file"
                    accept={acceptedTypes.join(',')}
                    onChange={handleFileInputChange}
                    className="hidden"
                    disabled={forceDisabled || disabled || isSubmitting}
                    {...registerProps}
                />

                {preview ? (
                    <div className="relative">
                        <img
                            src={preview}
                            alt="Podgląd"
                            className={classNames(
                                "w-36 h-36 object-cover rounded-2xl",
                                isDragOver && "ring-2 ring-primary"
                            )}
                        />
                    </div>
                ) : (
                    <div
                        className={classNames(
                            "w-[148px] h-[148px] rounded-3xl bg-[#EEF1F2] flex flex-col items-center justify-center select-none",
                            isDragOver && "ring-2 ring-primary bg-primary/5"
                        )}
                    >
                        <PlusIcon className="w-5 h-5 text-on-surface mb-4"/>
                        <span className="text-[14px] font-semibold text-on-surface text-center leading-tight">
                            Dodaj
                        </span>
                        <span className="text-[14px] font-semibold text-on-surface text-center leading-tight">
                            zdjęcie
                        </span>
                    </div>
                )}
            </div>

            {errors[name] && (
                <p className="text-red-500 text-xs pt-1">{String(errors[name]?.message)}</p>
            )}
        </div>
    );
};

export default ImageUploadFormField;
